import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ClipboardCheck, LifeBuoy, Building2 } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(',', '') : '';

const STATUS_BADGE = {
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  rejected: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

export default function AdminPartnerApplications() {
  const [data, setData] = useState(null);
  const [claims, setClaims] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conn, setConn] = useState({ email: '', city: '', venue_slug: '' });

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/admin/partner-applications`, auth()),
        axios.get(`${API}/admin/venue-claims`, auth()),
      ]);
      setData(a.data); setClaims(c.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load applications'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const decide = async (app, approve) => {
    const note = window.prompt(approve ? `Approve ${app.org_name}? Optional welcome note:` : `Reject ${app.org_name}? Tell them why (they'll see this):`);
    if (note === null) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/partner-applications/${app.app_id}/decide`, { approve, note }, auth());
      toast.success(approve ? `${app.org_name} approved — they've been emailed` : `${app.org_name} rejected`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const decideClaim = async (c, approve) => {
    const note = window.prompt(approve ? `Approve ${c.user_name} as manager of ${c.venue_name}?` : `Reject this claim? Tell them why:`);
    if (note === null) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/venue-claims/${c.claim_id}/decide`, { approve, note }, auth());
      toast.success(approve ? `${c.venue_name} connected to ${c.user_name}` : 'Claim rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const connectVenue = async () => {
    if (!conn.email || !conn.city || !conn.venue_slug) return toast.error('Email, city and venue slug are required');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/venue-profiles/connect`, conn, auth());
      toast.success(`${r.data.venue_name} connected to ${conn.email}`);
      setConn({ email: '', city: '', venue_slug: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Connect failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 space-y-6" data-testid="partner-apps-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-indigo-400" /> Partner Applications</h1>
          <p className="text-sm text-muted-foreground">Review venue and organizer applications, and see priority help requests.</p>
        </div>

        <div className="space-y-3">
          {data.applications.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}          {data.applications.map(a => (
            <Card key={a.app_id} className="border-border/60"><CardContent className="py-3 space-y-1.5" data-testid={`app-${a.app_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-sm">{a.org_name}</p>
                <Badge variant="outline" className="text-[10px]">{a.type}</Badge>
                <Badge className={`text-[10px] ${STATUS_BADGE[a.status] || ''}`}>{a.status}</Badge>
                <span className="text-[11px] text-muted-foreground ml-auto">{fmtDate(a.created_at)}</span>
              </div>
              <p className="text-xs text-muted-foreground">{a.user_name} · {a.user_email}{a.phone ? ` · ${a.phone}` : ''}{a.city ? ` · ${a.city}` : ''}{a.website ? ` · ${a.website}` : ''}</p>
              <p className="text-xs bg-muted/40 rounded-md p-2">{a.about}</p>
              {a.status === 'pending' && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => decide(a, true)} data-testid={`approve-${a.app_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-400" disabled={busy} onClick={() => decide(a, false)} data-testid={`reject-${a.app_id}`}>Reject</Button>
                </div>
              )}
            </CardContent></Card>
          ))}
        </div>

        <div>
          <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-fuchsia-400" /> Venue claims</p>
          <Card className="border-fuchsia-500/30 mb-3"><CardContent className="py-3 space-y-2">
            <p className="text-xs font-semibold">Connect a venue to a user (admin)</p>
            <div className="flex flex-wrap gap-2">
              <Input className="h-8 text-xs w-56" placeholder="User's account email" value={conn.email} onChange={e => setConn({ ...conn, email: e.target.value })} data-testid="connect-email" />
              <Input className="h-8 text-xs w-28" placeholder="City (denver)" value={conn.city} onChange={e => setConn({ ...conn, city: e.target.value })} data-testid="connect-city" />
              <Input className="h-8 text-xs w-52" placeholder="Venue slug (bierstadt-lagerhaus)" value={conn.venue_slug} onChange={e => setConn({ ...conn, venue_slug: e.target.value })} data-testid="connect-slug" />
              <Button size="sm" className="h-8 text-[11px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={busy} onClick={connectVenue} data-testid="connect-venue-btn">Connect venue</Button>
            </div>
            <p className="text-[10px] text-muted-foreground">The slug is the last part of the venue page URL. This approves them as a partner, creates their org, links the venue and emails them.</p>
          </CardContent></Card>
          <div className="space-y-2">
            {claims && claims.claims.length === 0 && <p className="text-xs text-muted-foreground">No venue claims yet.</p>}
            {claims && claims.claims.map(c => (
              <Card key={c.claim_id}><CardContent className="py-2.5 space-y-1" data-testid={`claim-${c.claim_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium">{c.venue_name} <span className="text-[11px] text-muted-foreground font-normal">({c.city})</span></p>
                  <Badge className={`text-[10px] ${STATUS_BADGE[c.status] || ''}`}>{c.status}</Badge>
                  <span className="text-[11px] text-muted-foreground ml-auto">{fmtDate(c.created_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{c.user_name} · {c.user_email}{c.message ? ` — "${c.message}"` : ''}</p>
                {c.status === 'pending' && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => decideClaim(c, true)} data-testid={`claim-approve-${c.claim_id}`}>Approve claim</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-400" disabled={busy} onClick={() => decideClaim(c, false)} data-testid={`claim-reject-${c.claim_id}`}>Reject</Button>
                  </div>
                )}
              </CardContent></Card>
            ))}
            {claims && claims.claimed_venues.length > 0 && (
              <p className="text-[11px] text-muted-foreground">Connected venues: {claims.claimed_venues.map(v => v.venue_name).join(', ')}</p>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><LifeBuoy className="h-4 w-4 text-indigo-400" /> Priority help requests</p>
          <div className="space-y-2">
            {data.help_requests.length === 0 && <p className="text-xs text-muted-foreground">No help requests.</p>}
            {data.help_requests.map(h => (
              <Card key={h.help_id}><CardContent className="py-2.5">
                <p className="text-sm font-medium">{h.subject} <span className="text-[11px] text-muted-foreground font-normal">— {h.user_name} ({h.user_email}) · {fmtDate(h.created_at)}</span></p>
                <p className="text-xs text-muted-foreground mt-1">{h.message}</p>
              </CardContent></Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
